import { Button, Col, Form, Row, Select, Upload, Modal, notification, message } from "antd";
import Box from '../../../common/components/Box'
import { ConfirmModal, FloatInput, FloatSelect, FloatTextArea } from '../../../common/components/Index'
import { useState } from 'react';
import FloatDatePicker from "../../../common/components/FloatDatePicker";
import { PlusCircleOutlined } from '@ant-design/icons';
import { BankResponse, CityResponse, DistrictResponse, fileTypes, UpdateUser, User, WardResponse } from "../../../common/types/types";
import { useEffect } from 'react';
import { useLazyGetFileByObjectIdAndTypeQuery, useUploadFileForObjectMutation, useUploadFileMutation } from "../../../../redux/api/FileApi";
import { profileIDTypes } from "../../types";
import { useLazyGetDistrictListQuery, useLazyGetWardListQuery } from "../../../../redux/api/SystemApi";
import { format } from "date-fns";
import { useUpdateProfileMutation } from "../../redux/ProfileApi";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import { patterns } from "../../../common/ts/CommonFunctions";
import { ReactComponent as PencilAlt } from '../../../../assets/images/pencil-alt.svg';
import OtpInsertModal from "./OtpInsertModal";
import { useAppDispatch } from "../../../../redux/Hooks";
import { setIncompleteProfile } from "../../../login/redux/LoginSlice";

const { Option } = Select;
const { confirm } = Modal;
const imageTypes = ['image/png', 'image/jpeg', 'image/png'];

interface FormProps {
    user?: User,
    provinceList: CityResponse[],
    bankList: BankResponse[],
}

const PersonalForm = ({ user, provinceList, bankList }: FormProps) => {
    const [form] = Form.useForm();
    const params = useParams();
    const navigate = useNavigate();
    const [count, setCount] = useState<any>([]);
    const [deleteIds, setDeleteIds] = useState<number[]>([]);
    const [imageId, setImageId] = useState<any>({});
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [wardList, setWardList] = useState<WardResponse[]>([]);
    const [districtList, setDistrictList] = useState<DistrictResponse[]>([]);
    const [isDirty, setIsDirty] = useState(false);
    const [otpVisible, setOtpVisible] = useState(false);

    const [uploadFile] = useUploadFileMutation();
    const [uploadFileForObject] = useUploadFileForObjectMutation();
    const [districtTrigger] = useLazyGetDistrictListQuery();
    const [wardTrigger] = useLazyGetWardListQuery();
    const [updateProfile] = useUpdateProfileMutation();
    const [fileTrigger] = useLazyGetFileByObjectIdAndTypeQuery();
    const location = useLocation();

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (params.redirect) {
            message.warning("Để thực hiện đăng ký bán tài sản hoặc tham gia đấu giá, vui lòng điền đầy đủ các thông tin dưới đây!", 5)
        }
    }, [params.redirect])

    useEffect(() => {
        if (form && user && Object.values(user).length) {
            let provinceId = user.province?.provinceId || user.district?.province?.provinceId || user.commune?.district?.province?.provinceId
            let districtId = user.district?.districtId || user.commune?.district?.districtId;
            let bankExists = user.bankId && bankList.findIndex(item => item.bankId === user.bankId) !== -1;

            form.setFieldsValue({
                fullName: user.fullName,
                sex: user.sex,
                dateOfBirth: user.dateOfBirth,
                phoneNumber: user.phoneNumber,
                email: user.email,
                city: provinceId,
                district: districtId,
                ward: user.commune?.communeId,
                identificationNumber: user.identificationNumber,
                identificationIssuedBy: user.identificationIssuedBy,
                identificationIssuedDate: user.identificationIssuedDate,
                bankAccount: user.bankAccount,
                bankBranch: user.bankBranch,
                bankId: bankExists ? user.bankId : user.bankName,
                bankNumber: user.bankNumber,
                address: user.address
            })

            setCount({
                fullName: user.fullName?.length || 0,
                phoneNumber: user.phoneNumber?.length || 0,
                email: user.email?.length || 0,
                identificationNumber: user.identificationNumber?.length || 0,
                identificationIssuedBy: user.identificationIssuedBy?.length || 0,
                bankAccount: user.bankAccount?.length || 0,
                bankBranch: user.bankBranch?.length || 0,
                bankName: user.bankName?.length || 0,
                bankNumber: user.bankNumber?.length || 0,
                address: user.address?.length || 0,
            })

            if (provinceId) {
                districtTrigger({ page: 0, size: 1000, provinceId: provinceId }).unwrap().then(res => {
                    setDistrictList([...res.content].sort((a, b) => a.districtName.localeCompare(b.districtName)));
                })
            }
            if (districtId) {
                wardTrigger({ page: 0, size: 1000, districtId }).unwrap().then(res => {
                    setWardList([...res.content].sort((a, b) => a.communeName.localeCompare(b.communeName)));
                })
            }

            // Lấy danh sách file
            fileTrigger({
                objectId: user.userId!,
                objectType: fileTypes.ACCOUNT
            }).unwrap().then(res => {
                let imageId: any = {};
                res.forEach(item => {
                    if (item.storageType === profileIDTypes.BACK || item.storageType === profileIDTypes.FRONT) {
                        imageId[item.storageType] = item.attachmentId;
                    }
                })
                setImageId(imageId);
            })
        }
    }, [user, form, districtTrigger, wardTrigger, fileTrigger, bankList])

    const onValuesChange = (changedValues: any) => {
        let key = Object.keys(changedValues)[0];
        let value = changedValues[key];
        if (typeof (value === "string") && value?.length) {
            setCount({
                ...count,
                [key]: value.length,
            });
        }

        if (key === "city") {
            if(changedValues.city) {
                districtTrigger({ page: 0, size: 1000, provinceId: changedValues.city }).unwrap().then(res => {
                    setDistrictList([...res.content].sort((a, b) => a.districtName.localeCompare(b.districtName)));
                })
            } else {
                setDistrictList([]);
            }
            setWardList([]);
            form.setFieldsValue({
                district: undefined,
                ward: undefined
            })
        }

        if (key === "district") {
            if(changedValues.district) {
                wardTrigger({ page: 0, size: 1000, districtId: changedValues.district }).unwrap().then(res => {
                    setWardList([...res.content].sort((a, b) => a.communeName.localeCompare(b.communeName)));
                })
            } else {
                setWardList([])
            }
            form.setFieldsValue({
                ward: undefined
            })
        }

        if (key === "bankNumber" && isNaN(Number(changedValues.bankNumber.charAt(changedValues.bankNumber.length - 1)))) {
            let newValue = changedValues.bankNumber.slice(0, -1);
            form.setFieldsValue({
                bankNumber: newValue
            })
            setCount({
                ...count,
                bankNumber: newValue.length,
            });
        }

        if (Object.values(changedValues)?.some(item => item)) {
            setIsDirty(true);
        } else {
            setIsDirty(false);
        }
    }

    const uploadButton = (
        <div>
            <PlusCircleOutlined style={{ fontSize: 40, color: '#c9c9c9' }} />
        </div>
    );

    const onFileRemove = (file: any) => {
        return new Promise<boolean>((resolve, reject) => {
            confirm({
                className: "custom-modal",
                title: 'Xoá file đã chọn?',
                onOk: () => {
                    if (file.response?.attachmentId) {
                        let newDeleteIds = deleteIds;
                        newDeleteIds.push(file.response.attachmentId)
                        setDeleteIds(newDeleteIds);
                    }
                    resolve(true);

                },
                onCancel: () => {
                    resolve(false)
                },
                okText: "Xác nhận",
                cancelText: "Hủy bỏ",
            })
        })
    }

    const beforeUpload = (files: File[]) => {
        let accepted = true;
        Array.from(files).forEach(file => {
            if (!imageTypes.includes(file.type)) {
                accepted = false;
                notification.error({
                    message: "Định dạng file không hợp lệ!",
                })
            } else if (file.size / (1024 * 1024) > 5) {
                accepted = false;
                notification.error({
                    message: "File tải lên vượt quá dung lượng 5MB!",
                })
            }
        })

        return accepted || Upload.LIST_IGNORE;
    }

    const normFile = (e: any) => {
        return e.fileList;
    };

    const customUpload = (options: any, type: string) => {
        options.onProgress({ percent: 0 })
        let formData = new FormData();
        formData.append('file', options.file);
        uploadFile({
            file: formData,
            objectType: fileTypes.ACCOUNT,
            storageType: type || "",
        }).unwrap()
            .then(res => {
                options.onProgress({ percent: 100 });
                options.onSuccess(res, options.file);
                setImageId({ ...imageId, [type]: res.attachmentId });
            }).catch(err => {
                options.onError();
            })
    }

    const toggleConfirmVisible = () => {
        setConfirmVisible(!confirmVisible);
    }

    const onFinish = (values: any) => {
        let submitValues: UpdateUser = {
            address: values.address,
            bankAccount: values.bankAccount,
            bankBranch: values.bankBranch,
            bankId: values.bankId,
            bankNumber: values.bankNumber,
            bankName: bankList.find(item => item.bankId === values.bankId)?.bankName || "",
            provinceId: values.city,
            districtId: values.district,
            communeId: values.ward,
            dateOfBirth: format(values.dateOfBirth, "yyyy-MM-dd'T'HH:mm:ss.SSS'+07:00'"),
            email: values.email,
            fullName: values.fullName,
            identificationNumber: values.identificationNumber,
            identificationIssuedBy: values.identificationIssuedBy,
            identificationIssuedDate: format(values.identificationIssuedDate, "yyyy-MM-dd'T'HH:mm:ss.SSS'+07:00'"),
            phoneNumber: values.phoneNumber,
            ...values.sex || values.sex === 0 ? { sex: values.sex } : {}
        }

        form.validateFields().then(res => {
            updateProfile(submitValues).unwrap().then(res => {
                notification.success({
                    message: "Cập nhật thông tin thành công!"
                })
                uploadFileAfterSubmit(res.userId!);

                // Đổi trạng thái hoàn thiện profile
                dispatch(setIncompleteProfile(false));
                if (params.redirect) {
                    if (location.state) {
                        // Nếu location có state thì redirect về url trong state
                        navigate(location.state as string);
                    } else {
                        // Nếu không có state thì redirect về trang trước đó
                        navigate(-1)
                    }
                }
            }).catch(err => {
                notification.error({
                    message: err.data?.message || "Đã có lỗi xảy ra. Vui lòng thử lại sau!"
                })
            })
        })

    }

    const uploadFileAfterSubmit = (id: number) => {
        uploadFileForObject({
            listFileIds: [imageId[profileIDTypes.FRONT], imageId[profileIDTypes.BACK]],
            objectId: id,
            objectType: fileTypes.ACCOUNT
        }).catch(err => {
            notification.error({
                message: err.data?.message || "Upload ảnh thất bại!"
            })
        })
    }

    const disabledDate = (date: moment.Moment) => {
        return (date && (date < moment('01/01/1900', 'DD/MM/Y') || date >= moment()))
    }

    const toggleOtpVisible = () => {
        setOtpVisible(!otpVisible);
    }

    return (
        <div>
            <Form
                onFinish={onFinish}
                className="custom-form"
                style={{ marginTop: 15, marginBottom: 45 }}
                name="personalRegister"
                form={form}
                colon={false}
                onValuesChange={onValuesChange}
                scrollToFirstError={true}
            >
                <Box>
                    <h3 style={{ fontWeight: 'bold' }}>Thông tin chung</h3>

                    <Row gutter={30}>
                        <Col span={12}>
                            <Form.Item
                                name="fullName"
                                rules={[{ required: true, message: "Vui lòng nhập họ và tên." }]}>
                                <FloatInput label="Họ và tên" placeholder="Họ và tên" maxLength={50} required allowClear />
                            </Form.Item>
                            <label className="input-show-count">{count.fullName || 0}/50</label>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="sex"
                            >
                                <FloatSelect label="Giới tính" placeholder="Giới tính" className="custom-select">
                                    <Option value={0}>Nam</Option>
                                    <Option value={1}>Nữ</Option>
                                    <Option value={2}>Khác</Option>
                                </FloatSelect>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={30}>
                        <Col span={12}>
                            <Form.Item
                                name="dateOfBirth"
                                style={{ marginBottom: 0 }}
                                rules={[{ required: true, message: "Vui lòng nhập ngày sinh." }]}>
                                <FloatDatePicker label="Ngày sinh" placeholder="Ngày sinh" className="filter-picker bg-white" disabledDate={disabledDate} required allowClear />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="phoneNumber"
                                rules={[{ required: true, message: "Vui lòng nhập Số điện thoại." },
                                { pattern: patterns.PHONE, message: "Số điện thoại không đúng." }
                                ]}>
                                <FloatInput label="Số điện thoại" placeholder="Số điện thoại" maxLength={12} required allowClear />
                            </Form.Item>
                            <label className="input-show-count">{count.phoneNumber || 0}/12</label>
                        </Col>
                    </Row>
                    <Row gutter={30}>
                        <Col span={12}>
                            <Form.Item
                                name="email"
                                rules={[{ required: true, message: "Vui lòng nhập email." }]}>
                                <FloatInput
                                    readOnly
                                    type="email"
                                    label="Email"
                                    placeholder="Email"
                                    maxLength={50}
                                    required
                                    suffix={<PencilAlt style={{ cursor: 'pointer' }} onClick={toggleOtpVisible} />} />
                            </Form.Item>
                            <label className="input-show-count">{count.email || 0}/50</label>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="identificationNumber"
                                rules={[{ required: true, message: "Vui lòng nhập CCCD/ CMND/ Hộ chiếu." }]}>
                                <FloatInput label="CCCD/ CMND/ Hộ chiếu" placeholder="CCCD/ CMND/ Hộ chiếu" maxLength={20} minLength={8} disabled />
                            </Form.Item>
                            <label className="input-show-count">{count.identificationNumber || 0}/20</label>
                        </Col>

                    </Row>
                    <Row gutter={30}>
                        <Col span={12}>
                            <Form.Item
                                name="identificationIssuedBy"
                                rules={[{ required: true, message: "Vui lòng nhập nơi cấp." }]}
                            >
                                <FloatInput label="Nơi cấp" placeholder="Nơi cấp" maxLength={200} required allowClear />
                            </Form.Item>
                            <label className="input-show-count">{count.identificationIssuedBy || 0}/20</label>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="identificationIssuedDate"
                                style={{ marginBottom: 0 }}
                                rules={[{ required: true, message: "Vui lòng nhập ngày cấp." }]}>
                                <FloatDatePicker label="Ngày cấp" placeholder="Ngày cấp" className="filter-picker bg-white" disabledDate={disabledDate} required allowClear />
                            </Form.Item>
                        </Col>
                    </Row>

                    <h3 style={{ marginTop: '30px' }}>Ảnh CCCD/ CMND/ Hộ chiếu<sup style={{ color: 'red', fontSize: 14 }}>*</sup>:</h3>
                    <Row gutter={30}>
                        <Col span={12}>
                            <Form.Item
                                style={{ marginBottom: 0 }}
                                name="front-upload"
                                valuePropName="fileList"
                                getValueFromEvent={normFile}
                                rules={[{
                                    required: !imageId[profileIDTypes.FRONT] && imageId[profileIDTypes.FRONT] !== 0,
                                    message: "Vui lòng upload ảnh."
                                }]}>
                                <Upload
                                    className="custom-upload"
                                    onRemove={(file) => onFileRemove(file)}
                                    customRequest={(options) => customUpload(options, profileIDTypes.FRONT)}
                                    accept="image/jpg,image/jpeg,image/png"
                                    beforeUpload={(file, fileList) => beforeUpload(fileList)}
                                    name="image"
                                    listType="picture-card"
                                    showUploadList={false}
                                    maxCount={1}
                                >
                                    {imageId[profileIDTypes.FRONT] || imageId[profileIDTypes.FRONT] === 0 ?
                                        <img
                                            src={process.env.REACT_APP_API_URL + `/files/downloadFile/${imageId[profileIDTypes.FRONT]}`}
                                            alt="avatar"
                                            style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : uploadButton
                                    }
                                </Upload>
                            </Form.Item>
                            <div style={{ textAlign: 'center' }}>Mặt trước</div>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                style={{ marginBottom: 0 }}
                                name="back-upload"
                                valuePropName="fileList"
                                getValueFromEvent={normFile}
                                rules={[{
                                    required: !imageId[profileIDTypes.BACK] && imageId[profileIDTypes.BACK] !== 0,
                                    message: "Vui lòng upload ảnh."
                                }]}>
                                <Upload
                                    className="custom-upload"
                                    onRemove={(file) => onFileRemove(file)}
                                    customRequest={(options) => customUpload(options, profileIDTypes.BACK)}
                                    accept="image/jpg,image/jpeg,image/png"
                                    beforeUpload={(file, fileList) => beforeUpload(fileList)}
                                    name="image"
                                    listType="picture-card"
                                    showUploadList={false}
                                    maxCount={1}
                                >
                                    {imageId[profileIDTypes.BACK] || imageId[profileIDTypes.BACK] === 0 ?
                                        <img
                                            src={process.env.REACT_APP_API_URL + `/files/downloadFile/${imageId[profileIDTypes.BACK]}`}
                                            alt="avatar"
                                            style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : uploadButton
                                    }
                                </Upload>
                            </Form.Item>
                            <div style={{ textAlign: 'center' }}>Mặt sau</div>
                        </Col>
                    </Row>

                    <h3 style={{ marginTop: '30px' }}>Địa chỉ:</h3>
                    <Row gutter={30}>
                        <Col span={8}>
                            <Form.Item
                                name="city"
                                rules={[{ required: true, message: "Vui lòng nhập Tỉnh/Thành phố." }]}>
                                <FloatSelect
                                    allowClear
                                    required
                                    label="Tỉnh/Thành phố"
                                    placeholder="Tỉnh/Thành phố"
                                    className="custom-select"
                                    options={provinceList.map(item => ({
                                        label: item.provinceName,
                                        value: item.provinceId
                                    }))}
                                ></FloatSelect>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="district"
                                rules={[{ required: true, message: "Vui lòng nhập Quận/Huyện." }]}>
                                <FloatSelect
                                    allowClear
                                    required
                                    label="Quận/Huyện"
                                    placeholder="Quận/Huyện"
                                    className="custom-select"
                                    options={districtList.map(item => ({
                                        label: item.districtName,
                                        value: item.districtId
                                    }))}
                                ></FloatSelect>
                            </Form.Item>
                        </Col>

                        <Col span={8}>
                            <Form.Item
                                name="ward"
                                rules={[{ required: true, message: "Vui lòng nhập Phường/Xã." }]}>
                                <FloatSelect
                                    allowClear
                                    required
                                    label="Phường/Xã"
                                    placeholder="Phường/Xã"
                                    className="custom-select"
                                    options={wardList.map(item => ({
                                        label: item.communeName,
                                        value: item.communeId
                                    }))}
                                >
                                </FloatSelect>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={30}>
                        <Col span={24}>
                            <Form.Item
                                name="address"
                                rules={[{ required: true, message: "Vui lòng nhập nhập địa chỉ chi tiết." }]}>
                                <FloatTextArea label="Nhập địa chỉ chi tiết" placeholder="Nhập địa chỉ chi tiết" maxLength={200} rows={1} required allowClear />
                            </Form.Item>
                            <label className="input-show-count">{count.address || 0}/200</label>
                        </Col>
                    </Row>

                </Box>
                <div style={{ marginTop: '10px' }}>
                    <Box>
                        <h3 style={{ fontWeight: 'bold' }}>Thông tin tài khoản ngân hàng (nhận hoàn tiền đặt trước)</h3>
                        <Row gutter={30}>
                            <Col span={12}>
                                <Form.Item
                                    name="bankNumber"
                                    rules={[{ required: true, message: "Vui lòng nhập STK ngân hàng." }]}>
                                    <FloatInput label="STK ngân hàng" placeholder="STK ngân hàng" maxLength={20} required allowClear />
                                </Form.Item>
                                <label className="input-show-count">{count.bankNumber || 0}/20</label>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="bankId"
                                    rules={[{ required: true, message: "Vui lòng nhập ngân hàng." },
                                    {
                                        validator(rule, value, callback) {
                                            if (bankList.findIndex(item => item.bankId === value) !== -1 || value == null) {
                                                return Promise.resolve()
                                            }
                                            return Promise.reject(new Error('Ngân hàng không hợp lệ, vui lòng chọn ngân hàng khác.'))
                                        },
                                    }]}>
                                    {/* <FloatInput label="Ngân hàng" placeholder="Ngân hàng" maxLength={100} required allowClear /> */}
                                    <FloatSelect
                                        optionFilterProp="label"
                                        showSearch
                                        allowClear
                                        required
                                        label="Ngân hàng"
                                        placeholder="Ngân hàng"
                                        className="custom-select"
                                        options={bankList.map(item => ({
                                            label: item.bankName,
                                            value: item.bankId
                                        }))}
                                    ></FloatSelect>
                                </Form.Item>
                                {/* <label className="input-show-count">{count.bankName || 0}/100</label> */}
                            </Col>
                        </Row>
                        <Row gutter={30}>
                            <Col span={12}>
                                <Form.Item
                                    name="bankAccount"
                                    rules={[{ required: true, message: "Vui lòng nhập chủ tài khoản." }]}>
                                    <FloatInput label="Chủ tài khoản" placeholder="Chủ tài khoản" maxLength={50} required allowClear />
                                </Form.Item>
                                <label className="input-show-count">{count.bankAccount || 0}/50</label>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="bankBranch"
                                    rules={[{ required: true, message: "Vui lòng nhập chi nhánh." }]}>
                                    <FloatInput label="Chi nhánh" placeholder="Chi nhánh" maxLength={50} required allowClear />
                                </Form.Item>
                                <label className="input-show-count">{count.bankBranch || 0}/50</label>
                            </Col>
                        </Row>
                    </Box>

                    <Row style={{ marginTop: 45 }} align="middle" justify="center">
                        <Button
                            className={isDirty ? "button-light-yellow" : "button-light-gray"}
                            style={{ height: 50, width: 240, fontSize: 16 }}
                            onClick={() => toggleConfirmVisible()}
                            disabled={!isDirty}>Cập nhật</Button>
                    </Row>
                </div>
                {confirmVisible ?
                    <ConfirmModal
                        onCancel={toggleConfirmVisible}
                        onOk={() => {
                            form.submit();
                            toggleConfirmVisible();
                        }}
                        status="error"
                        title="Bạn có chắc chắn muốn thay đổi thông tin?"
                        cancelText="Huỷ bỏ"
                        okText="Đồng ý"
                        visible={confirmVisible}
                    /> : <></>}

                {otpVisible ?
                    <OtpInsertModal
                        form={form}
                        visible={otpVisible}
                        toggleVisible={toggleOtpVisible}
                    /> : <></>}
            </Form >
        </div>
    )
}

export default PersonalForm